import { useAccountSelector, WalletData } from '@/design-system/components/accountSelector/useAccountSelector';
import { AlertType, UniAlert } from '@/design-system/components/alerts';
import { UniButton } from '@/design-system/components/buttons';
import { ButtonVariant } from '@/design-system/components/buttons/useButton.hook';
import { Icon } from '@/design-system/components/icons/Icon';
import { H6, TextCaption, TextTitle } from '@/design-system/components/typography/UniTypography';
import { DATE_FORMAT, formatDate } from '@/utils/dateFormatting';
import BottomSheet, { BottomSheetBackdrop, BottomSheetScrollView, useBottomSheetDynamicSnapPoints } from '@gorhom/bottom-sheet';
import { StackActions, useNavigation } from '@react-navigation/native';
import { Pressable, ScrollView, SxProp, useDripsyTheme, View } from 'dripsy';
import _, { isString } from 'lodash';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useWindowDimensions } from 'react-native';
import { useStore } from 'store/store';
import { RootStackRoutes } from 'types';
import { IntervalBillPayment, ScheduleBillPayment } from '../../enum';
import { useBillPaymentForm } from '../../hooks/useBillPaymentForm.hook';
import { useCreateBillPayment } from '../../hooks/useCreateBillPayment.hook';
import { BillPaymentConfirmProp, BillPaymentStackRoutes } from '../../payment.navigator';
import { displayAmountWithCurrency, openImage64, openURL } from '../../utils/utils';
import { AttachmentViewer } from './attachmentViewer/AttachmentViewer';
import { BillPaymentInfoItem } from './BillPaymentInfoItem';

interface ConfirmFormBillPaymentProps {
  bottomsheetRef: React.RefObject<BottomSheet>;
  dataProp?: BillPaymentConfirmProp;
  onClose: () => void;
  onResult: () => void;
  debitAccountCallback?: (input: string) => void;
  setUserFeeCallBank?: (input: string) => void;
}

export const ConfirmFormBillPayment: React.FC<ConfirmFormBillPaymentProps> = (props) => {
  const { t } = useTranslation('global-bill-payment');
  const store = useStore();
  const { bottomsheetRef, dataProp, onClose, onResult, debitAccountCallback, setUserFeeCallBank } = props;
  const { request, response, debitAccount, flagUploaded } = dataProp || {};
  const navigation = useNavigation();
  const [isShowingHandle, setShowingHandle] = useState(true);
  const [debitAccountApi, setDebitAccountApi] = useState<string>('');
  const { theme } = useDripsyTheme();
  const { width, height } = useWindowDimensions();
  const { $4 } = theme.space || ({} as any);
  const widthScaleImage = width - ($4 as number) * 2;
  const heightScaleImage = (widthScaleImage / 343) * 250;
  const {
    detailForm: { setAssetName, setAmount },
    general: { debitAmount, billPaymentFee, userFee, isLoadingFee }
  } = useBillPaymentForm();
  const {
    general: { alertMessage, setAlertMessage, getThumbnailUrLFromResponse },
    postCreateBillPayment: { postSubmit, mapInstructionSubmitRequest, isLoadingSubmit }
  } = useCreateBillPayment();

  const onValueChange = (value: string) => {};
  const onWalletChange = (value: WalletData | undefined) => {
    if (value) {
      setDebitAccountApi(`${value.label}\n${value.accountNumber}`);
    }
  };

  useEffect(() => {
    if (!!response && !!response.attributes) {
      setAmount(response.attributes.amount!.toString());
      setAssetName(response.attributes.asset!);
    }
  }, [response]);

  useEffect(() => {
    if (!!debitAccountCallback) debitAccountCallback(!!debitAccount ? debitAccount : debitAccountApi);
  }, [debitAccount, debitAccountApi]);

  useEffect(() => {
    if (!!setUserFeeCallBank) setUserFeeCallBank(userFee ?? '');
  }, [userFee]);

  const {} = useAccountSelector({
    asset: response?.attributes?.asset,
    value: response?.attributes?.source?.attributes?.walletId,
    type: 'currency',
    onValueChange,
    onWalletChange
  });

  const { animatedHandleHeight, animatedSnapPoints, animatedContentHeight, handleContentLayout } = useBottomSheetDynamicSnapPoints([
    'CONTENT_HEIGHT',
    'CONTENT_HEIGHT'
  ]);

  const renderBackdrop = useCallback(
    (props) => (
      <BottomSheetBackdrop {...props} disappearsOnIndex={-1} appearsOnIndex={0} pressBehavior={'none'} enableTouchThrough={true} />
    ),
    []
  );

  const navigationToForm = () => {
    onClose();
    setTimeout(() => {
      if (!!request && !!response)
        navigation.navigate(RootStackRoutes.BillPayment, {
          screen: BillPaymentStackRoutes.Form,
          params: {
            request,
            response,
            documentSelected: dataProp?.documentSelected,
            paymentType: dataProp?.paymentType!
          }
        });
    }, 200);
  };

  const onConfirm = () => {
    if (request && response) {
      if (store.users && (store.users.tfaKeyStatus || store.users.phoneTfaKeyStatus)) {
        onResult();
      } else {
        setAlertMessage('');
        postSubmit(
          mapInstructionSubmitRequest(request, response, null),
          (submitData) => {
            onClose();
            setTimeout(() => {
              navigation.dispatch(
                StackActions.replace(RootStackRoutes.BillPayment, {
                  screen: BillPaymentStackRoutes.Success,
                  params: {
                    request,
                    response,
                    debitAccount: !_.isEmpty(debitAccount) ? debitAccount : debitAccountApi,
                    documentSelected: dataProp?.documentSelected,
                    submitResponse: submitData.data,
                    paymentType: dataProp?.paymentType,
                    userFee
                  }
                })
              );
            }, 200);
          },
          (error) => {
            const data: any = error.response?.data;
            const errorMessage = data && isString(data.message) ? t(data.message) : error.message;
            setAlertMessage(errorMessage);
          }
        );
      }
    }
  };

  if (_.isUndefined(dataProp) || _.isUndefined(request) || _.isUndefined(response) || _.isUndefined(debitAccount)) return <></>;

  const renderScheduleDate = () => {
    if (_.isEmpty(response.attributes?.schedule)) {
      return <></>;
    }

    return (
      <>
        {response.attributes?.schedule &&
        response.attributes?.schedule.type?.toLowerCase() !== ScheduleBillPayment.RECURRING.toLowerCase() ? (
          <BillPaymentInfoItem
            title={t('global-bill-payment::glossary.scheduled-date')}
            description={
              !!response.attributes?.schedule?.endDate
                ? formatDate(response.attributes?.schedule?.endDate, { format: DATE_FORMAT.NO_TIME })
                : ' - '
            }
          />
        ) : (
          <BillPaymentInfoItem
            title={t('global-bill-payment::glossary.date-of-first-payment')}
            description={
              !!response.attributes?.schedule?.startDate
                ? formatDate(response.attributes?.schedule?.startDate, { format: DATE_FORMAT.NO_TIME })
                : ' - '
            }
          />
        )}
      </>
    );
  };

  const isRenderImage = (): boolean => {
    if (
      (!!response && !_.isEmpty(getThumbnailUrLFromResponse(response))) ||
      (dataProp?.documentSelected &&
        dataProp?.documentSelected.type === 'success' &&
        (dataProp?.documentSelected.mimeType == 'image/png' || dataProp?.documentSelected.mimeType == 'image/jpeg'))
    )
      return true;
    return false;
  };

  const renderImage = () => {
    if (!!response && !_.isEmpty(getThumbnailUrLFromResponse(response))) {
      return (
        <Pressable onPress={async () => openURL(getThumbnailUrLFromResponse(response))}>
          <AttachmentViewer width={widthScaleImage} height={heightScaleImage} imageUrl={getThumbnailUrLFromResponse(response)} />
        </Pressable>
      );
    }
    if (
      dataProp?.documentSelected &&
      dataProp?.documentSelected.type === 'success' &&
      (dataProp?.documentSelected.mimeType == 'image/png' || dataProp?.documentSelected.mimeType == 'image/jpeg')
    ) {
      return (
        <Pressable onPress={() => openImage64(dataProp.documentSelected!.uri)}>
          <AttachmentViewer width={widthScaleImage} height={heightScaleImage} imageUrl={dataProp.documentSelected.uri} />
        </Pressable>
      );
    }
    return <></>;
  };

  const renderDebitAccount = () => {
    if (!_.isEmpty(debitAccount)) {
      return (
        <BillPaymentInfoItem
          title={t('global-bill-payment::glossary.debit-account')}
          description={debitAccount}
          titleSx={sxStyles.viewFlex1}
          descriptionSx={sxStyles.viewFlex2}
        />
      );
    }
    if (!_.isEmpty(debitAccountApi)) {
      return (
        <BillPaymentInfoItem
          title={t('global-bill-payment::glossary.debit-account')}
          description={debitAccountApi}
          titleSx={sxStyles.viewFlex1}
          descriptionSx={sxStyles.viewFlex2}
        />
      );
    }
    return (
      <BillPaymentInfoItem
        title={t('global-bill-payment::glossary.debit-account')}
        description={'-'}
        titleSx={sxStyles.viewFlex1}
        descriptionSx={sxStyles.viewFlex2}
      />
    );
  };

  return (
    <BottomSheet
      index={-1}
      ref={bottomsheetRef}
      snapPoints={animatedSnapPoints}
      handleHeight={animatedHandleHeight}
      contentHeight={animatedContentHeight}
      backdropComponent={renderBackdrop}
      enablePanDownToClose
      onClose={onClose}
      onChange={(index: number) => {
        setShowingHandle(index == 1);
      }}
      handleComponent={() => (
        <View sx={sxStyles.bottomSheetHeadWrapper}>
          {isShowingHandle && <View sx={sxStyles.viewHandle} />}
          <View sx={sxStyles.bottomsheetHeadHandler}></View>
        </View>
      )}>
      <BottomSheetScrollView onLayout={handleContentLayout} scrollEnabled={true}>
        <View sx={sxStyles.container}>
          <ScrollView sx={{ height: isRenderImage() ? height : 'auto' }}>
            <View sx={sxStyles.bodyWrapper}>
              <View>
                <BottomSheetTopHeader title={t('global-bill-payment::confirmation.title--bill-payment-confirmation')} onPress={onClose} />
              </View>
              <TextCaption sx={sxStyles.description} semiBold={true}>
                {t('global-bill-payment::confirmation.title--please-confirm')}
              </TextCaption>

              {_.isNumber(widthScaleImage) && _.isNumber(heightScaleImage) && <View sx={sxStyles.marginTop8}>{renderImage()}</View>}
              <TextCaption semiBold={true} sx={sxStyles.textLabel}>
                {t('global-bill-payment::glossary.merchant-bank-name')}
              </TextCaption>
              <H6 bold={true} sx={sxStyles.textValueBold}>
                {response?.attributes?.destination?.attributes?.recipient}
              </H6>

              <TextCaption semiBold={true} sx={sxStyles.textLabel}>
                {t('global-bill-payment::glossary.bill-amount')}
              </TextCaption>
              <H6 bold={true} sx={sxStyles.textValueBold}>
                {displayAmountWithCurrency(response.attributes?.amount?.toString() ?? '', response.attributes?.asset!)}
              </H6>

              <BillPaymentInfoItem
                title={t('global-bill-payment::glossary.bill-bank-account-number')}
                description={response?.attributes?.destination?.attributes?.accountNumber ?? ''}
                titleSx={sxStyles.viewFlex1}
                descriptionSx={sxStyles.viewFlex1}
                sx={{ mt: '$5' }}
              />
              <BillPaymentInfoItem
                title={t('global-bill-payment::glossary.payment-additional-details')}
                description={
                  _.isEmpty(response?.attributes?.destination?.attributes?.paymentInfo)
                    ? '-'
                    : response?.attributes?.destination?.attributes?.paymentInfo ?? ''
                }
                titleSx={sxStyles.viewFlex1}
                descriptionSx={sxStyles.viewFlex1}
              />
              {renderDebitAccount()}
              <BillPaymentInfoItem
                title={t('global-bill-payment::glossary.instruction-fee')}
                description={`${billPaymentFee}`}
                descriptionSx={{ color: '$danger' }}
              />
              <BillPaymentInfoItem
                title={t('global-bill-payment::glossary.debit-amount')}
                description={debitAmount}
                descriptionSx={{ color: '$danger' }}
              />
              {renderScheduleDate()}
              {!_.isEmpty(response.attributes?.schedule?.interval) && (
                <BillPaymentInfoItem
                  title={t('global-bill-payment::glossary.frequency')}
                  description={
                    response.attributes?.schedule?.interval === IntervalBillPayment.DAILY
                      ? t('global-bill-payment::glossary.daily')
                      : response.attributes?.schedule?.interval !== IntervalBillPayment.WEEKLY
                      ? t('global-bill-payment::glossary.monthly')
                      : t('global-bill-payment::glossary.weekly')
                  }
                />
              )}
              {response.attributes?.schedule &&
                response.attributes?.schedule.type?.toLowerCase() === ScheduleBillPayment.RECURRING.toLowerCase() && (
                  <BillPaymentInfoItem
                    title={t('global-bill-payment::glossary.payment-end-date')}
                    description={
                      !!response.attributes?.schedule?.endDate
                        ? formatDate(response.attributes?.schedule?.endDate, { format: DATE_FORMAT.NO_TIME })
                        : ' - '
                    }
                  />
                )}
              <BillPaymentInfoItem
                title={t('global-bill-payment::glossary.remarks')}
                description={_.isEmpty(response.remarks) ? '-' : response.remarks!}
                titleSx={sxStyles.viewFlex1}
                descriptionSx={sxStyles.viewFlex2}
              />
              {!_.isEmpty(alertMessage) && <UniAlert type={AlertType.Error} message={alertMessage} sx={sxStyles.viewAlert} />}
              <View sx={{ ...sxStyles.button, ...{ mb: isRenderImage() ? '$5' : 0 } }}>
                {!flagUploaded && (
                  <>
                    <View sx={sxStyles.flex131}>
                      <UniButton
                        label={t('global-bill-payment::general.edit-info')}
                        onPress={() => {
                          navigationToForm();
                        }}
                        variant={ButtonVariant.outline1}
                        sx={sxStyles.viewButtonWrapper}
                        size="medium"
                        labelSx={sxStyles.textBlack}
                      />
                    </View>
                    <View sx={sxStyles.flex16} />
                  </>
                )}

                <View sx={sxStyles.flex220}>
                  <UniButton
                    label={t('global-bill-payment::general.place-instruction')}
                    onPress={onConfirm}
                    sx={sxStyles.viewButtonWrapper}
                    size="medium"
                    loading={isLoadingSubmit}
                    disabled={isLoadingSubmit || isLoadingFee}
                  />
                </View>
              </View>
            </View>
          </ScrollView>
        </View>
      </BottomSheetScrollView>
    </BottomSheet>
  );
};

const sxStyles: Record<string, SxProp> = {
  container: {
    flex: 1,
    backgroundColor: '$backgroundPrimary'
  },
  bottomSheetHeadWrapper: {
    alignItems: 'center',
    flexDirection: 'column'
  },
  viewHandle: {
    width: '$12',
    height: '$1',
    backgroundColor: '$backgroundPrimary',
    borderRadius: 3,
    position: 'absolute',
    top: -10
  },
  bottomsheetHeadHandler: {
    height: '$5',
    borderTopLeftRadius: 10,
    borderTopRightRadius: 10,
    backgroundColor: '$backgroundPrimary',
    borderWidth: 0
  },
  viewDivider: {
    height: 1,
    backgroundColor: '$borderDivider',
    mt: 18,
    mb: '$5'
  },
  description: {
    mt: '$6'
  },
  contentItem: {
    mt: '$3',
    flexDirection: 'row'
  },
  textLeft: {
    color: '#666666',
    flex: 1
  },
  textRight: {
    flex: 1,
    textAlign: 'right'
  },
  button: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    mt: '$5'
  },
  bodyWrapper: {
    flexDirection: 'column',
    mr: '$4',
    ml: '$4',
    mb: '$3',
    flex: 1
  },
  textLabel: {
    color: '#666666',
    mt: '$5'
  },
  textValue: {
    color: '$secondary',
    mt: '$1'
  },
  viewButtonWrapper: {
    height: '$11',
    width: '100%'
  },
  textBlack: {
    color: '$black'
  },
  marginTop20: {
    mt: '$5'
  },
  marginTop8: {
    mt: '$2'
  },
  flex16: {
    flex: 16
  },
  flex131: {
    flex: 131
  },
  flex220: {
    flex: 220
  },
  textValueBold: {
    color: '$secondary',
    mt: '$1'
  },
  viewFlex1: {
    flex: 1
  },
  viewFlex2: {
    flex: 2
  },
  imageWrapper: {
    mt: '$2',
    mb: '$5'
  },
  viewAlert: {
    mt: '$3'
  }
};

interface BottomSheetTopHeaderProps {
  title: string;
  onPress: () => void;
}

export const BottomSheetTopHeader: React.FC<BottomSheetTopHeaderProps> = (props) => {
  const { title, onPress } = props;

  return (
    <View sx={sxTopHeader.container}>
      <TextTitle semiBold sx={sxTopHeader.title}>
        {title}
      </TextTitle>
      <Pressable onPress={onPress} sx={sxTopHeader.iconClose}>
        <View>
          <Icon name="cross" themeColor="$iconDefault" />
        </View>
      </Pressable>
    </View>
  );
};

const sxTopHeader: Record<string, SxProp> = {
  container: {
    flexDirection: 'row'
  },
  title: {
    flex: 4
  },
  iconClose: {
    flex: 1,
    alignContent: 'flex-end',
    alignItems: 'flex-end'
  }
};
